import React, { Fragment, useEffect } from 'react'

import styles from '../style.module.css'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useRegistrationCertificatesStore } from '../stores/registrationCertificateStore'
import { useRegisteredImagesStore } from '../stores/registeredImagesStore'
import { Typography } from '@material-ui/core'
import {
	deleteRegistrationCertificate,
	getRegistrationCertificateFileLink,
} from '../api'
import Button from '../../../generic/Button'
import { toast } from 'sonner'
import { UploadImagesModal } from './UploadImagesModal' // Your parent store

export const RegistrationCertificateDetails = ({ match, history }) => {
	const { registrationCertificateId } = match.params

	const {
		removeRegistrationCertificate,
		incrementRegistrationCertificateImageCount,
	} = useRegistrationCertificatesStore()

	const {
		registeredImages,
		totalRegisteredImagesCount,
		searchString,
		fetchRegisteredImages,
		selectedRegistrationCertificate,
		deleteImageId,
		setSearchString,
		setUploadImagesModalOpen,
	} = useRegisteredImagesStore()

	useEffect(() => {
		const fetchRegisteredImagesPromise = async () => {
			await fetchRegisteredImages(registrationCertificateId, {
				isInitialLoad: true,
			})
		}
		fetchRegisteredImagesPromise()
	}, [registrationCertificateId])

	const handleSearch = () => {
		fetchRegisteredImages(registrationCertificateId, {
			isInitialLoad: true,
			searchString,
		})
	}

	const handleKeyDown = async (event) => {
		if (event.key === 'Enter') {
			await handleSearch()
		}
	}

	const handleLoadMore = () => {
		fetchRegisteredImages(registrationCertificateId)
	}

	const handleDeleteImage = async (registeredImageId) => {
		try {
			await deleteImageId({
				registeredImageId,
				registrationCertificateId,
				clientId: selectedRegistrationCertificate.client_id,
			})
			incrementRegistrationCertificateImageCount({
				registrationCertificateId,
				incrementImageCountBy: -1,
			})
			await toast.success('Successfully deleted Image')
		} catch (e) {
			await toast.error(
				'Something went wrong when deleting image. Please try again later.',
			)
		}
	}

	const viewCertificate = async () => {
		try {
			const response = await getRegistrationCertificateFileLink({
				registrationCertificateId,
			})

			if (!response || !response.url) {
				await toast.error(
					'Could not open the certificate. Please try again later.',
				)
				return
			}

			window.open(response.url, '_blank')
		} catch (e) {
			console.error(e)
			await toast.error(
				'Could not open the certificate. Please try again later.',
			)
		}
	}

	// The link is pasted into Pipedrive, so it has to point at the certificate
	// itself rather than depend on which partner the viewer has selected.
	const copyDirectLink = async () => {
		const directLink = `${window.location.origin}/registration-certificates/${registrationCertificateId}`
		try {
			await navigator.clipboard.writeText(directLink)
			await toast.success('Link copied to clipboard')
		} catch (e) {
			console.error(e)
			await toast.error(`Could not copy the link. The link is: ${directLink}`)
		}
	}

	const handleDeleteRegistrationCertificate = async ({
		registrationCertificateId,
	}) => {
		try {
			await deleteRegistrationCertificate({
				registrationCertificateId,
				clientId: selectedRegistrationCertificate.client_id,
			})

			history.push('/registration-certificates')
			removeRegistrationCertificate(registrationCertificateId)
			await toast.success('Registration certificate deleted successfully')
		} catch (e) {
			console.error(e)
			await toast.error(
				'Failed to delete registration certificate. Please try again later.',
			)
		}
	}

	return (
		<div className={styles.subPageContainer}>
			{selectedRegistrationCertificate ? (
				<Fragment>
					<UploadImagesModal />
					<div className={styles.titleContainer}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
							<div>
								<Typography variant='body2' className={styles.listTitle}>
									{
										selectedRegistrationCertificate.registration_certificate_number
									}
								</Typography>
								<Typography variant='body2' fontColor='rgb(142, 147, 155)'>
									Partner:{' '}
									{selectedRegistrationCertificate.client_name ||
										'Unknown partner'}
								</Typography>
								<Typography variant='body2' fontColor='rgb(142, 147, 155)'>
									Effective Date:{' '}
									{selectedRegistrationCertificate.effective_registration_date}
								</Typography>
								{selectedRegistrationCertificate.gcs_uri ? (
									<button
										style={{ border: 'none', cursor: 'pointer' }}
										onClick={viewCertificate}
									>
										<Typography fontWeight={600}>
											Click here to view certificate
										</Typography>
									</button>
								) : (
									<Typography variant='body2' fontColor='rgb(142, 147, 155)'>
										No certificate uploaded
									</Typography>
								)}
							</div>
						</div>
						<div style={{ display: 'flex', gap: '10px', height: 'fit-content' }}>
							<Button onClick={copyDirectLink}>Copy link</Button>
							<Button
								onClick={() =>
									handleDeleteRegistrationCertificate({
										registrationCertificateId:
											selectedRegistrationCertificate.registration_certificate_id,
									})
								}
							>
								Delete Certificate
							</Button>
						</div>
					</div>

					<div className={styles.titleContainer} style={{ marginBottom: 0 }}>
						<div>
							<Typography variant='body2' className={styles.listTitle}>
								Images
							</Typography>
							<Typography
								variant='body2'
								fontColor='rgb(142, 147, 155)'
								style={{ marginBottom: '16px' }}
							>
								{totalRegisteredImagesCount} Images
							</Typography>
						</div>
						<Button onClick={() => setUploadImagesModalOpen(true)}>
							Upload Images
						</Button>
					</div>

					<div className={styles.searchContainer}>
						<input
							className={styles.searchInput}
							value={searchString}
							onKeyDown={handleKeyDown}
							onChange={(e) => setSearchString(e.target.value)}
							placeholder={'Search for Image'}
						/>
						<Button
							style={{ marginLeft: '16px', marginBottom: 0 }}
							onClick={handleSearch}
						>
							Search for images
						</Button>
					</div>
					<div
						id='partnerImagesList'
						className={styles.listContainer}
						style={{
							height: 'calc(100vh - 370px)',
						}}
					>
						{registeredImages.length > 0 ? (
							<InfiniteScroll
								className={styles.infiniteScrollWrap}
								dataLength={registeredImages.length}
								next={handleLoadMore}
								hasMore={registeredImages.length < totalRegisteredImagesCount}
								loader={
									<div key={0} className={styles.loadMoreWrap}>
										Loading...
									</div>
								}
								scrollableTarget='partnerImagesList'
							>
								{registeredImages.map((registeredImage, index) => (
									<div style={{ display: 'flex' }} key={index}>
										<div className={styles.card}>
											<div
												style={{
													display: 'flex',
													justifyContent: 'space-between',
												}}
											>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: '20px',
													}}
												>
													<Typography
														variant='body2'
														fontColor='rgb(142, 147, 155)'
													>
														Image:
													</Typography>
													<Typography variant='body2'>
														{registeredImage.partner_image_id}
													</Typography>
												</div>
												<button
													onClick={() =>
														handleDeleteImage(
															registeredImage.registered_image_id,
														)
													}
												>
													Remove Image
												</button>
											</div>
											<div className={styles.rowDivider} />
										</div>
									</div>
								))}
							</InfiniteScroll>
						) : (
							<div className={styles.loadingContainer}>
								<Typography variant='body2' fontColor='rgb(142, 147, 155)'>
									No images found.
								</Typography>
							</div>
						)}
					</div>
				</Fragment>
			) : (
				<div className={styles.loadingContainer}>
					<div style={{ textAlign: 'center' }}>
						<Typography variant='body2'>
							Registration Certificate could not be found.
						</Typography>
						<Typography variant='body2' fontColor='rgb(142, 147, 155)'>
							Try to select another registration certificate to the left.
						</Typography>
					</div>
				</div>
			)}
		</div>
	)
}

export const NoRegistrationCertificateSelected = () => {
	return (
		<div className={styles.subPageContainer}>
			<div className={styles.loadingContainer}>
				<div style={{ textAlign: 'center' }}>
					<Typography variant='body2'>
						No Registration Certificate Selected
					</Typography>
					<Typography variant='body2' fontColor='rgb(142, 147, 155)'>
						Select a registration certificate from the list to the left.
					</Typography>
				</div>
			</div>
		</div>
	)
}
